import { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useEngSignedUrl } from '@/features/engineering/hooks';

// three.js + the CAD kernel are only pulled in when someone opens a model.
const ModelViewer = lazy(() => import('./ModelViewer'));

export interface ModelViewerTarget {
  /** Storage key inside the eng-files bucket. */
  storagePath: string;
  fileName: string;
  /** Title shown in the dialog header, e.g. "bowl.step · v3". */
  title?: string;
  subtitle?: string;
}

interface ModelViewerDialogProps {
  target: ModelViewerTarget | null;
  onClose: () => void;
}

export function ModelViewerDialog({ target, onClose }: ModelViewerDialogProps) {
  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex h-[88vh] w-[96vw] max-w-6xl flex-col gap-0 overflow-hidden p-0"
        dismissOnOutsideClick
      >
        <div className="border-b border-border px-5 py-3 pr-12">
          <DialogTitle className="truncate text-base">{target?.title ?? target?.fileName ?? '3D preview'}</DialogTitle>
          <DialogDescription className="truncate text-xs">
            {target?.subtitle ?? 'Rotate, zoom and measure the part. Nothing here changes the file.'}
          </DialogDescription>
        </div>
        <div className="min-h-0 flex-1">
          {target && <ViewerBody target={target} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ViewerBody({ target }: { target: ModelViewerTarget }) {
  const { data: url, isLoading, error } = useEngSignedUrl(target.storagePath);
  if (isLoading || !url) {
    return (
      <Centered>
        {error ? (
          <p className="text-sm text-muted-foreground">Could not get a download link for this file.</p>
        ) : (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-info" />
            <p className="text-sm text-muted-foreground">Preparing preview…</p>
          </>
        )}
      </Centered>
    );
  }
  return (
    <Suspense
      fallback={
        <Centered>
          <Loader2 className="h-6 w-6 animate-spin text-info" />
          <p className="text-sm text-muted-foreground">Loading 3D viewer…</p>
        </Centered>
      }
    >
      <ModelViewer source={{ url }} fileName={target.fileName} className="h-full" />
    </Suspense>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 bg-[#f4f5f3] px-6 text-center">
      {children}
    </div>
  );
}
